import React from "react";
import { Field, TextField, LongText, RadioField, CheckboxGroupField, InlineCheck, InfoPanel, SectionTitle } from "./fields";
import { FileUpload } from "./FileUpload";
import { useSequentialReveal, ScrollLock } from "./reveal";
import { MATTER_CATEGORIES, MAX_UPLOAD_HINT } from "../../lib/options";
import { INTAKE } from "../../constants/testIds";
import { Button } from "../ui/button";
import { useBackGuard, hasAnswers } from "./useBackGuard";
import { RequiredDatesNote } from "./RequiredDatesNote";

const DECISION_TYPES = [
  "A final judgment or final order",
  "An interim or temporary order",
  "A decision made during an ongoing case",
  "A refusal to make an order",
  "A costs order",
  "An enforcement decision",
  "An order made when I was not present",
  "A refusal of permission to appeal",
  "Another decision - please state",
  "I am not sure",
];
const COURTS = ["Family Court", "High Court", "Court of Appeal", "Supreme Court", "Magistrates’ Court", "County Court", "Another court or tribunal - please state", "I do not know"];
const OUTCOMES = [
  "The decision should be reversed.",
  "The order should be changed.",
  "There should be a new hearing.",
  "The case should be reconsidered by a different judge.",
  "The order should be suspended temporarily.",
  "The reasons or wording should be clarified or corrected.",
  "I want to understand whether there may be grounds for a challenge.",
  "Another outcome — please state.",
  "I am not sure.",
];
const APPLIED_STEPS = [
  "Suspension or stay of the order",
  "An extension of time to appeal",
  "Permission to appeal",
  "Reconsideration",
  "The order to be set aside",
  "The order to be varied",
  "Correction of an accidental error",
  "Clarification of the judgment or order",
  "None",
  "I do not know",
];
const HELP = [
  "Initial guidance about whether the decision may be challenged",
  "Guidance about whether an appeal or another application is appropriate",
  "Guidance about permission to appeal",
  "Drafting grounds of appeal",
  "Drafting an appeal notice or application",
  "Guidance about extending the time to appeal",
  "Guidance about suspending the existing order",
  "Representation at a permission hearing",
  "Representation at an appeal hearing",
  "Guidance about responding to another party’s appeal",
  "Review of documents already prepared",
  "Other — please state",
  "I am not sure",
];

export const RouteD = ({ form, set, config, onBack, onSubmit, submitting }) => {
  const d = form.routeD || {};
  const u = (k, v) => set("routeD", { ...d, [k]: v });
  const maxMb = config?.max_file_size_mb || 10;
  const wantsRep = (d.help || []).some((h) => h.startsWith("Representation"));
  if (wantsRep !== !!form.linked_route_a) set("linked_route_a", wantsRep);
  const { requestBack, dialog } = useBackGuard(hasAnswers(d), onBack);
  const missingDates = [];
  if (!d.decision_date && !d.decision_date_unknown) missingDates.push("d-decision-date");
  if (!d.received_date && !d.received_none && !d.received_unknown) missingDates.push("d-received-date");
  if (d.has_appeal_deadline === "Yes" && !d.appeal_deadline_date && !d.appeal_deadline_date_unknown) missingDates.push("d-deadline-date");
  if (d.urgent_action === "Yes" && !d.urgent_by_date && !d.urgent_by_date_unknown) missingDates.push("d-urgent-by-date");
  if (d.how_made === "At a hearing I did not attend" && !d.learned_date && !d.learned_date_unknown) missingDates.push("d-learned-date");
  if (d.asked_permission === "Yes" && !d.permission_date && !d.permission_date_unknown) missingDates.push("d-permission-date");
  if (d.already_filed === "Yes" && !d.filed_date && !d.filed_date_unknown) missingDates.push("d-filed-date");
  if (d.other_appealed === "Yes" && !d.other_deadline && !d.other_deadline_unknown) missingDates.push("d-other-deadline");
  const datesOk = missingDates.length === 0;
  const otherOk =
    (!(d.decision_type || []).includes("Another decision - please state") || !!(d.decision_type_other && d.decision_type_other.trim())) &&
    (!(d.matter || []).includes("Other — please state") || !!(d.matter_other && d.matter_other.trim())) &&
    (d.court !== "Another court or tribunal - please state" || !!(d.court_other && d.court_other.trim())) &&
    (!(d.outcomes || []).includes("Another outcome — please state.") || !!(d.outcome_other && d.outcome_other.trim())) &&
    (!(d.help || []).includes("Other — please state") || !!(d.help_other && d.help_other.trim())) &&
    (d.represented !== "Other — please state" || !!(d.represented_other && d.represented_other.trim()));

  const qFlags = [
    !!d.decision_date || !!d.decision_date_unknown,                // 0  q1
    !!d.received_date || !!d.received_none || !!d.received_unknown, // 1  q2
    !!d.has_appeal_deadline,                                        // 2  q3
    !!d.urgent_action,                                             // 3  q4
    (d.decision_type || []).length > 0,                           // 4  q5
    (d.matter || []).length > 0,                                 // 5  q6
    !!d.court,                                                    // 6  q7
    !!d.how_made,                                                 // 7  q8
    !!d.have_order,                                               // 8  q9
    !!d.have_reasons,                                             // 9  q10
    !!d.have_transcript,                                          // 10 q11
    !!d.why_challenge || !!d.why_challenge_unsure,                // 11 q12
    !!d.effect || !!d.effect_skip,                                // 12 q13 (optional)
    (d.outcomes || []).length > 0,                               // 13 q14
    !!d.asked_permission,                                        // 14 q15
    !!d.already_filed,                                           // 15 q16
    !!d.other_appealed,                                          // 16 q17
    (d.applied_steps || []).length > 0,                         // 17 q18
    !!d.represented,                                            // 18 q19
    (d.help || []).length > 0,                                 // 19 q21
    !!d.accessibility || !!d.no_accessibility,                 // 20 q22
  ];
  const sFlags = qFlags;
  const sVisible = useSequentialReveal(qFlags);

  return (
    <div className="space-y-8" data-testid="route-d-form">
      {dialog}
      <SectionTitle>Challenging or appealing a decision</SectionTitle>

      <InfoPanel tone="gold" testid="d-intro">
        This route is for enquiries about challenging a judgment, court order or other court decision.
        Appeal time limits can be short and may run from the date the decision was made rather than the date
        on which the written order was received. Completing this form does not pause or extend any court
        deadline. Depending on the circumstances, the appropriate step may be an appeal or an application to
        set aside, vary, clarify, correct or reconsider an order.
      </InfoPanel>

      {/* D2 */}
      {sVisible > 0 && (
      <div className="space-y-5">
        <h4 className="font-serif text-lg text-brand-gold">Decision and deadline</h4>
        <Field num={1} show={sVisible > 0} scroll={false} label="When was the decision made?" required={!d.decision_date_unknown}>
          <TextField type="date" value={d.decision_date} onChange={(v) => u("decision_date", v)} disabled={d.decision_date_unknown} testid="d-decision-date" />
          <InlineCheck checked={d.decision_date_unknown} onChange={(v) => { u("decision_date_unknown", !!v); set("routeD", { ...d, decision_date_unknown: !!v, decision_recent_unknown_deadline: !!v }); }} label="I do not know" testid="d-decision-date-unknown" />
        </Field>
        <Field num={2} show={sVisible > 1} label="When did you receive the written judgment or court order?" required={!(d.received_none || d.received_unknown)}>
          <TextField type="date" value={d.received_date} onChange={(v) => u("received_date", v)} disabled={d.received_none || d.received_unknown} testid="d-received-date" />
          <div className="flex gap-6 flex-wrap">
            <InlineCheck checked={d.received_none} onChange={(v) => u("received_none", !!v)} label="I have not received it" testid="d-received-none" />
            <InlineCheck checked={d.received_unknown} onChange={(v) => u("received_unknown", !!v)} label="I do not know" testid="d-received-unknown" />
          </div>
        </Field>
        <Field num={3} show={sVisible > 2} label="Is there a deadline for filing an appeal or another application?">
          <RadioField options={["Yes", "No", "I do not know"]} value={d.has_appeal_deadline} onChange={(v) => u("has_appeal_deadline", v)} name="d-has-deadline" testid="d-has-deadline" />
          {d.has_appeal_deadline === "Yes" && (
            <div className="space-y-4 border-l-2 border-brand-gold pl-4 mt-3"><ScrollLock />
              <Field label="Deadline date" required={!d.appeal_deadline_date_unknown}>
                <TextField type="date" value={d.appeal_deadline_date} onChange={(v) => u("appeal_deadline_date", v)} disabled={d.appeal_deadline_date_unknown} testid="d-deadline-date" />
                <InlineCheck checked={d.appeal_deadline_date_unknown} onChange={(v) => u("appeal_deadline_date_unknown", !!v)} label="I do not know" testid="d-deadline-date-unknown" />
              </Field>
              <Field label="Deadline time, if stated"><TextField type="time" value={d.appeal_deadline_time} onChange={(v) => u("appeal_deadline_time", v)} testid="d-deadline-time" /></Field>
              <Field label="Where the deadline is recorded" optional><TextField value={d.deadline_source} onChange={(v) => u("deadline_source", v)} testid="d-deadline-source" /></Field>
            </div>
          )}
        </Field>
        <Field num={4} show={sVisible > 3} label="Does the order require anything to happen urgently?">
          <RadioField options={["Yes", "No", "I do not know"]} value={d.urgent_action} onChange={(v) => u("urgent_action", v)} name="d-urgent-action" testid="d-urgent-action" />
          {d.urgent_action === "Yes" && (
            <div className="space-y-4 border-l-2 border-brand-gold pl-4 mt-3"><ScrollLock />
              <Field label="What must happen?"><LongText value={d.urgent_what} onChange={(v) => u("urgent_what", v)} testid="d-urgent-what" /></Field>
              <Field label="Who must do it?"><TextField value={d.urgent_who} onChange={(v) => u("urgent_who", v)} testid="d-urgent-who" /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="By what date" required={!d.urgent_by_date_unknown}>
                  <TextField type="date" value={d.urgent_by_date} onChange={(v) => u("urgent_by_date", v)} disabled={d.urgent_by_date_unknown} testid="d-urgent-by-date" />
                  <InlineCheck checked={d.urgent_by_date_unknown} onChange={(v) => u("urgent_by_date_unknown", !!v)} label="I do not know" testid="d-urgent-by-date-unknown" />
                </Field>
                <Field label="By what time"><TextField type="time" value={d.urgent_by_time} onChange={(v) => u("urgent_by_time", v)} testid="d-urgent-by-time" /></Field>
              </div>
              <Field label="Is enforcement action threatened or underway?"><RadioField options={["Yes", "No", "I do not know"]} value={d.enforcement} onChange={(v) => u("enforcement", v)} name="d-enforcement" testid="d-enforcement" /></Field>
            </div>
          )}
        </Field>
      </div>

      )}
      {/* D3 */}
      {sVisible > 4 && (
      <div className="space-y-5">
        <h4 className="font-serif text-lg text-brand-gold">Nature of the decision</h4>
        <Field num={5} show={sVisible > 4} label="What type of decision do you want to challenge?" hint="Select all that apply.">
          <CheckboxGroupField options={DECISION_TYPES} value={d.decision_type} onChange={(v) => u("decision_type", v)} exclusive={["I am not sure"]} testid="d-decision-type" extras={{
            "Another decision - please state": (d.decision_type || []).includes("Another decision - please state") ? (<div><TextField value={d.decision_type_other} onChange={(v) => u("decision_type_other", v)} placeholder="Please state *" testid="d-decision-type-other" />{!(d.decision_type_other && d.decision_type_other.trim()) && <p className="text-xs text-brand-danger mt-1">Please complete this field.</p>}</div>) : null,
          }} />
        </Field>
        <Field num={6} show={sVisible > 5} label="What is the case about?" hint="Select all that apply.">
          <CheckboxGroupField options={MATTER_CATEGORIES} value={d.matter} onChange={(v) => u("matter", v)} testid="d-matter" extras={{
            "Other — please state": (d.matter || []).includes("Other — please state") ? (<div><TextField value={d.matter_other} onChange={(v) => u("matter_other", v)} placeholder="Please state *" testid="d-matter-other" />{!(d.matter_other && d.matter_other.trim()) && <p className="text-xs text-brand-danger mt-1">Please complete this field.</p>}</div>) : null,
          }} />
        </Field>
        <Field num={7} show={sVisible > 6} label="Which court made the decision?">
          <RadioField options={COURTS} value={d.court} onChange={(v) => u("court", v)} name="d-court" testid="d-court" after={{
            "Another court or tribunal - please state": d.court === "Another court or tribunal - please state" ? (<div className="pl-8"><TextField value={d.court_other} onChange={(v) => u("court_other", v)} placeholder="Please state *" testid="d-court-other" />{!(d.court_other && d.court_other.trim()) && <p className="text-xs text-brand-danger mt-1">Please complete this field.</p>}</div>) : null,
          }} />
        </Field>
        <Field num={8} show={sVisible > 7} label="How was the decision made?">
          <RadioField options={["At a hearing I attended", "At a hearing I did not attend", "Without a hearing", "On paper", "By agreement or consent", "I do not know"]} value={d.how_made} onChange={(v) => u("how_made", v)} name="d-how-made" testid="d-how-made" />
          {d.how_made === "At a hearing I did not attend" && (
            <div className="space-y-4 border-l-2 border-brand-gold pl-4 mt-3"><ScrollLock />
              <Field label="Did you know about the hearing?"><RadioField options={["Yes", "No", "I do not know"]} value={d.knew_hearing} onChange={(v) => u("knew_hearing", v)} name="d-knew" testid="d-knew" /></Field>
              <Field label="Why were you unable to attend?"><LongText value={d.why_absent} onChange={(v) => u("why_absent", v)} testid="d-why-absent" /></Field>
              <Field label="Did you ask for an adjournment?"><RadioField options={["Yes", "No", "I do not know"]} value={d.asked_adjourn} onChange={(v) => u("asked_adjourn", v)} name="d-adjourn" testid="d-adjourn" /></Field>
              <Field label="When did you first learn that the order had been made?" required={!d.learned_date_unknown}>
                <TextField type="date" value={d.learned_date} onChange={(v) => u("learned_date", v)} disabled={d.learned_date_unknown} testid="d-learned-date" />
                <InlineCheck checked={d.learned_date_unknown} onChange={(v) => u("learned_date_unknown", !!v)} label="I do not know" testid="d-learned-date-unknown" />
              </Field>
              <InfoPanel tone="grey">This enquiry will be flagged for consideration of a possible set-aside application.</InfoPanel>
            </div>
          )}
        </Field>
      </div>

      )}
      {/* D4 Documents */}
      {sVisible > 8 && (
      <div className="space-y-5">
        <h4 className="font-serif text-lg text-brand-gold">Documents</h4>
        <Field num={9} show={sVisible > 8} label="Do you have the court order?">
          <RadioField options={["Yes, a sealed or approved order", "Yes, a draft or unsealed order", "No", "I am not sure"]} value={d.have_order} onChange={(v) => u("have_order", v)} name="d-have-order" testid="d-have-order" />
          <div className="mt-2"><FileUpload value={d.order_files} onChange={(v) => u("order_files", v)} maxMb={maxMb} testid="d-order-upload" /></div>
        </Field>
        <Field num={10} show={sVisible > 9} label="Do you have the judgment or the judge’s reasons?">
          <RadioField options={["Yes, written reasons", "Reasons were given orally", "I have requested the reasons", "No reasons have been provided", "I do not know"]} value={d.have_reasons} onChange={(v) => u("have_reasons", v)} name="d-have-reasons" testid="d-have-reasons" />
          <div className="mt-2"><FileUpload value={d.reasons_files} onChange={(v) => u("reasons_files", v)} maxMb={maxMb} testid="d-reasons-upload" /></div>
        </Field>
        <Field num={11} show={sVisible > 10} label="Do you have a transcript or note of the hearing?">
          <RadioField options={["Official transcript", "My legal representative’s note", "My own note", "I have requested a transcript", "None", "I do not know"]} value={d.have_transcript} onChange={(v) => u("have_transcript", v)} name="d-have-transcript" testid="d-have-transcript" />
        </Field>
      </div>

      )}
      {/* D5 Reasons and outcome */}
      {sVisible > 11 && (
      <div className="space-y-5">
        <h4 className="font-serif text-lg text-brand-gold">Reasons and desired outcome</h4>
        <Field num={12} show={sVisible > 11} label="Why do you believe the decision should be challenged?">
          <LongText value={d.why_challenge} onChange={(v) => u("why_challenge", v)} disabled={d.why_challenge_unsure} testid="d-why-challenge" />
          <InlineCheck checked={d.why_challenge_unsure} onChange={(v) => u("why_challenge_unsure", !!v)} label="I am not sure and would like the decision reviewed" testid="d-why-challenge-unsure" />
        </Field>
        <Field num={13} show={sVisible > 12} label="What effect has the decision had, or will it have, on you or any child concerned?">
          <LongText value={d.effect} onChange={(v) => u("effect", v)} testid="d-effect" />
          {!(d.effect || d.effect_skip) && (
            <Button type="button" variant="outline" onClick={() => u("effect_skip", true)} data-testid="d-effect-continue" className="mt-3 rounded-sm border-brand-gold text-brand-black hover:bg-brand-gold/10">
              Continue / Skip this step
            </Button>
          )}
        </Field>
        <Field num={14} show={sVisible > 13} label="What outcome are you seeking?" hint="Select all that apply.">
          <CheckboxGroupField options={OUTCOMES} value={d.outcomes} onChange={(v) => u("outcomes", v)} exclusive={["I am not sure."]} testid="d-outcomes" extras={{
            "Another outcome — please state.": (d.outcomes || []).includes("Another outcome — please state.") ? (<div><TextField value={d.outcome_other} onChange={(v) => u("outcome_other", v)} placeholder="Please state *" testid="d-outcome-other" />{!(d.outcome_other && d.outcome_other.trim()) && <p className="text-xs text-brand-danger mt-1">Please complete this field.</p>}</div>) : null,
          }} />
        </Field>
      </div>

      )}
      {/* D6 Permission and steps */}
      {sVisible > 14 && (
      <div className="space-y-5">
        <h4 className="font-serif text-lg text-brand-gold">Permission and steps already taken</h4>
        <Field num={15} show={sVisible > 14} label="Did you ask the judge for permission to appeal?">
          <RadioField options={["Yes", "No", "I do not know", "I was not present"]} value={d.asked_permission} onChange={(v) => u("asked_permission", v)} name="d-asked-permission" testid="d-asked-permission" />
          {d.asked_permission === "Yes" && (
            <div className="space-y-4 border-l-2 border-brand-gold pl-4 mt-3"><ScrollLock />
              <Field label="Was permission granted, refused, partly granted or not yet decided?"><RadioField options={["Granted", "Refused", "Partly granted", "Not yet decided"]} value={d.permission_result} onChange={(v) => u("permission_result", v)} name="d-permission-result" testid="d-permission-result" /></Field>
              <Field label="Date of that decision" required={!d.permission_date_unknown}>
                <TextField type="date" value={d.permission_date} onChange={(v) => u("permission_date", v)} disabled={d.permission_date_unknown} testid="d-permission-date" />
                <InlineCheck checked={d.permission_date_unknown} onChange={(v) => u("permission_date_unknown", !!v)} label="I do not know" testid="d-permission-date-unknown" />
              </Field>
              <Field label="Upload the relevant order, if available" optional><FileUpload value={d.permission_files} onChange={(v) => u("permission_files", v)} maxMb={maxMb} testid="d-permission-upload" /></Field>
            </div>
          )}
        </Field>
        <Field num={16} show={sVisible > 15} label="Has an appeal notice or another application challenging the decision already been filed?">
          <RadioField options={["Yes", "No", "I do not know"]} value={d.already_filed} onChange={(v) => u("already_filed", v)} name="d-already-filed" testid="d-already-filed" />
          {d.already_filed === "Yes" && (
            <div className="space-y-4 border-l-2 border-brand-gold pl-4 mt-3"><ScrollLock />
              <Field label="Type of document filed"><TextField value={d.filed_type} onChange={(v) => u("filed_type", v)} testid="d-filed-type" /></Field>
              <Field label="Date filed" required={!d.filed_date_unknown}>
                <TextField type="date" value={d.filed_date} onChange={(v) => u("filed_date", v)} disabled={d.filed_date_unknown} testid="d-filed-date" />
                <InlineCheck checked={d.filed_date_unknown} onChange={(v) => u("filed_date_unknown", !!v)} label="I do not know" testid="d-filed-date-unknown" />
              </Field>
              <Field label="Court"><TextField value={d.filed_court} onChange={(v) => u("filed_court", v)} testid="d-filed-court" /></Field>
              <Field label="Appeal or application number, if issued"><TextField value={d.filed_number} onChange={(v) => u("filed_number", v)} testid="d-filed-number" /></Field>
              <Field label="Current status"><TextField value={d.filed_status} onChange={(v) => u("filed_status", v)} testid="d-filed-status" /></Field>
              <Field label="Next deadline"><TextField type="date" value={d.filed_next_deadline} onChange={(v) => u("filed_next_deadline", v)} testid="d-filed-next-deadline" /></Field>
              <Field label="Document upload" optional><FileUpload value={d.filed_files} onChange={(v) => u("filed_files", v)} maxMb={maxMb} testid="d-filed-upload" /></Field>
            </div>
          )}
        </Field>
        <Field num={17} show={sVisible > 16} label="Has another party appealed or said that they intend to appeal?">
          <RadioField options={["Yes", "No", "I do not know"]} value={d.other_appealed} onChange={(v) => u("other_appealed", v)} name="d-other-appealed" testid="d-other-appealed" />
          {d.other_appealed === "Yes" && (
            <div className="space-y-4 border-l-2 border-brand-gold pl-4 mt-3"><ScrollLock />
              <Field label="Which party?"><TextField value={d.other_party} onChange={(v) => u("other_party", v)} testid="d-other-party" /></Field>
              <Field label="Nature of their challenge"><LongText value={d.other_nature} onChange={(v) => u("other_nature", v)} testid="d-other-nature" /></Field>
              <Field label="Response deadline" required={!d.other_deadline_unknown}>
                <TextField type="date" value={d.other_deadline} onChange={(v) => u("other_deadline", v)} disabled={d.other_deadline_unknown} testid="d-other-deadline" />
                <InlineCheck checked={d.other_deadline_unknown} onChange={(v) => u("other_deadline_unknown", !!v)} label="I do not know" testid="d-other-deadline-unknown" />
              </Field>
              <Field label="Relevant documents" optional><FileUpload value={d.other_files} onChange={(v) => u("other_files", v)} maxMb={maxMb} testid="d-other-upload" /></Field>
            </div>
          )}
        </Field>
        <Field num={18} show={sVisible > 17} label="Have you applied for any of the following?" hint="Select all that apply.">
          <CheckboxGroupField options={APPLIED_STEPS} value={d.applied_steps} onChange={(v) => u("applied_steps", v)} exclusive={["None", "I do not know"]} testid="d-applied-steps" />
          {(d.applied_steps || []).filter((s) => s !== "None" && s !== "I do not know").length > 0 && (
            <Field label="For the step(s) selected, please give the application date, current status, next hearing or deadline, and note any documents you can upload above.">
              <LongText value={d.applied_details} onChange={(v) => u("applied_details", v)} testid="d-applied-details" />
            </Field>
          )}
        </Field>
      </div>

      )}
      {/* D7 Previous representation */}
      {sVisible > 18 && (
      <div className="space-y-5">
        <h4 className="font-serif text-lg text-brand-gold">Previous representation</h4>
        <Field num={19} show={sVisible > 18} label="Were you represented when the decision was made?">
          <RadioField options={["Yes, by a solicitor and barrister", "Yes, by a solicitor", "Yes, by a directly instructed barrister", "No, I represented myself", "I had assistance from another person or organisation", "Other — please state"]} value={d.represented} onChange={(v) => u("represented", v)} name="d-represented" testid="d-represented" after={{
            "Other — please state": d.represented === "Other — please state" ? (<div className="pl-8"><TextField value={d.represented_other} onChange={(v) => u("represented_other", v)} placeholder="Please state *" testid="d-represented-other" />{!(d.represented_other && d.represented_other.trim()) && <p className="text-xs text-brand-danger mt-1">Please complete this field.</p>}</div>) : null,
          }} />
          {(d.represented || "").startsWith("Yes") && (
            <div className="space-y-4 border-l-2 border-brand-gold pl-4 mt-3"><ScrollLock />
              <Field label="Are those representatives still acting?"><RadioField options={["Yes", "No", "I do not know"]} value={d.reps_acting} onChange={(v) => u("reps_acting", v)} name="d-reps-acting" testid="d-reps-acting" /></Field>
              <Field label="Have they advised you about a possible appeal?"><RadioField options={["Yes", "No", "I do not know"]} value={d.reps_advised} onChange={(v) => u("reps_advised", v)} name="d-reps-advised" testid="d-reps-advised" /></Field>
            </div>
          )}
        </Field>
      </div>

      )}
      {/* D8 Assistance */}
      {sVisible > 19 && (
      <div className="space-y-5">
        <h4 className="font-serif text-lg text-brand-gold">Assistance required</h4>
        <Field num={21} show={sVisible > 19} label="What help would you like?" hint="Select all that apply.">
          <CheckboxGroupField options={HELP} value={d.help} onChange={(v) => u("help", v)} exclusive={["I am not sure"]} testid="d-help" extras={{
            "Other — please state": (d.help || []).includes("Other — please state") ? (<div><TextField value={d.help_other} onChange={(v) => u("help_other", v)} placeholder="Please state *" testid="d-help-other" />{!(d.help_other && d.help_other.trim()) && <p className="text-xs text-brand-danger mt-1">Please complete this field.</p>}</div>) : null,
          }} />
          {wantsRep && (
            <InfoPanel tone="gold" testid="d-link-a">
              Because you have asked for representation at a listed hearing, we will create a linked hearing
              representation enquiry and only ask about anything still outstanding.
            </InfoPanel>
          )}
        </Field>
      </div>

      )}
      {/* D9 Accessibility */}
      {sVisible > 20 && (
      <div className="space-y-5">
        <h4 className="font-serif text-lg text-brand-gold">Accessibility</h4>
        <Field num={22} show={sVisible > 20} label="Communication or accessibility requirements">
          <LongText value={d.accessibility} onChange={(v) => u("accessibility", v)} disabled={d.no_accessibility} testid="d-accessibility" />
          <InlineCheck checked={d.no_accessibility} onChange={(v) => u("no_accessibility", !!v)} label="No additional requirements" testid="d-no-accessibility" />
        </Field>
      </div>

      )}
      {sVisible >= sFlags.length && !datesOk && (
        <RequiredDatesNote missing={missingDates} />
      )}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <Button variant="ghost" onClick={requestBack} data-testid={INTAKE.back} className="text-brand-darkgrey rounded-sm">Back</Button>
        {sVisible >= sFlags.length && (
          <Button onClick={onSubmit} disabled={submitting || !datesOk || !otherOk} data-testid={INTAKE.submit} className="bg-brand-black text-white hover:bg-brand-gold rounded-sm px-8">
            {submitting ? "Submitting…" : "Submit enquiry"}
          </Button>
        )}
      </div>
    </div>
  );
};
